/**
 * YouTube Gallery Component
 * Displays latest videos from the Rialto Channel YouTube channel
 */

import { useState, useEffect } from '@wordpress/element';
import apiFetch from '@wordpress/api-fetch';

export default function YouTubeGallery({ maxResults = 8 }) {
    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeVideo, setActiveVideo] = useState(null);

    useEffect(() => {
        fetchVideos();
    }, []);

    const fetchVideos = async () => {
        try {
            setLoading(true);
            const data = await apiFetch({
                path: `rialto/v1/youtube/channel-videos?max_results=${maxResults}`,
            });
            setVideos(data.videos || []);
            setError(null);
        } catch (err) {
            console.error('YouTube gallery error:', err);
            setError(err.message || 'Failed to load videos');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-NZ', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
        });
    };

    const handleVideoClick = (video) => {
        if (activeVideo && activeVideo.id === video.id) {
            setActiveVideo(null);
        } else {
            setActiveVideo(video);
        }
    };

    if (loading) {
        return (
            <div className="rialto-yt-gallery">
                <div className="rialto-yt-loading">
                    <div className="rialto-netflix-spinner" />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="rialto-yt-gallery">
                <div className="rialto-yt-error">
                    <p>Failed to load videos. Please try again.</p>
                    <button onClick={fetchVideos} className="rialto-btn rialto-btn-primary">
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    if (videos.length === 0) {
        return null;
    }

    return (
        <div className="rialto-yt-gallery">
            <div className="rialto-yt-header">
                <h2 className="rialto-yt-title">
                    <svg width="24" height="24" fill="currentColor" viewBox="0 0 24 24" className="rialto-yt-icon">
                        <path d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.4.6A3 3 0 0 0 .5 6.2 31.4 31.4 0 0 0 0 12a31.4 31.4 0 0 0 .5 5.8 3 3 0 0 0 2.1 2.1c1.9.6 9.4.6 9.4.6s7.5 0 9.4-.6a3 3 0 0 0 2.1-2.1A31.4 31.4 0 0 0 24 12a31.4 31.4 0 0 0-.5-5.8zM9.6 15.6V8.4l6.3 3.6-6.3 3.6z"/>
                    </svg>
                    Rialto Channel
                </h2>
                <a
                    href="https://www.youtube.com/@Rialtochannel/videos"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rialto-yt-subscribe-link"
                >
                    View Channel
                </a>
            </div>

            {/* Inline Player */}
            {activeVideo && (
                <div className="rialto-yt-player">
                    <button
                        className="rialto-yt-player-close"
                        onClick={() => setActiveVideo(null)}
                        aria-label="Close player"
                    >
                        &times;
                    </button>
                    <div className="rialto-yt-player-wrapper">
                        <iframe
                            src={`${activeVideo.embed_url}?autoplay=1&rel=0`}
                            title={activeVideo.title}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        />
                    </div>
                    <h3 className="rialto-yt-player-title">{activeVideo.title}</h3>
                </div>
            )}

            {/* Video Grid */}
            <div className="rialto-yt-grid">
                {videos.map((video) => (
                    <button
                        key={video.id}
                        className={`rialto-yt-card ${activeVideo && activeVideo.id === video.id ? 'active' : ''}`}
                        onClick={() => handleVideoClick(video)}
                    >
                        <div className="rialto-yt-card-thumb">
                            <img
                                src={video.thumbnail}
                                alt={video.title}
                                loading="lazy"
                            />
                            <div className="rialto-yt-card-play">
                                <svg width="40" height="40" viewBox="0 0 24 24" fill="white">
                                    <polygon points="5 3 19 12 5 21 5 3" />
                                </svg>
                            </div>
                        </div>
                        <div className="rialto-yt-card-info">
                            <h4 className="rialto-yt-card-title">{video.title}</h4>
                            <span className="rialto-yt-card-date">{formatDate(video.published_at)}</span>
                        </div>
                    </button>
                ))}
            </div>
        </div>
    );
}
